import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Map } from "@neshan-maps-platform/ol";
import NeshanMap, {
  NeshanMapRef,
} from "@neshan-maps-platform/react-openlayers";
import Feature from "@neshan-maps-platform/ol/Feature";
import Point from "@neshan-maps-platform/ol/geom/Point";
import VectorSource from "@neshan-maps-platform/ol/source/Vector";
import Cluster from "@neshan-maps-platform/ol/source/Cluster";
import VectorLayer from "@neshan-maps-platform/ol/layer/Vector";
import Style from "@neshan-maps-platform/ol/style/Style";
import CircleStyle from "@neshan-maps-platform/ol/style/Circle";
import Fill from "@neshan-maps-platform/ol/style/Fill";
import Stroke from "@neshan-maps-platform/ol/style/Stroke";
import Text from "@neshan-maps-platform/ol/style/Text";
import { fromLonLat } from "@neshan-maps-platform/ol/proj";
import {
  boundingExtent,
  getCenter,
  getHeight,
  getWidth,
} from "@neshan-maps-platform/ol/extent";
import { easeOut } from "@neshan-maps-platform/ol/easing";
import clsx from "clsx";
import { Icon } from "components/Icon";
import { useURLTools } from "utils/URLtools";
import { TMeterMapPoint } from "../types";

/**
 * Same key and base type as MapLocationPicker_v2 — the map configuration proven
 * in production. Do NOT copy these from components/Map_openLayers: that
 * component is dead code (its only remaining call site is commented out) and its
 * key does not serve tiles, which renders a blank map.
 */
const NESHAN_MAP_KEY = "web.13887c11a0624be0b9ceb86184273df9";
const NESHAN_MAP_TYPE = "dreamy";

/** Tehran — the view until the first batch of points arrives. */
const FALLBACK_CENTER = { latitude: 35.705803, longitude: 51.4217049 };

/** Cluster radius in screen pixels while clustering is on. */
const CLUSTER_DISTANCE = 46;
/**
 * Must stay 0. OpenLayers documents that a non-zero `minDistance` stops a
 * cluster feature from sitting at the center of its members — it gets nudged
 * apart from neighbouring bubbles instead. That makes a bubble appear over
 * dots it does not contain, which reads as a wrong count. Overlapping bubbles
 * are the lesser evil.
 */
const CLUSTER_MIN_DISTANCE = 0;
/** At and above this zoom, clustering switches off and every meter gets its own dot. */
export const CLUSTER_OFF_ZOOM = 15;

const MIN_ZOOM = 5;
const MAX_ZOOM = 18;

/**
 * Marker colors, all from the Tailwind brand palette, and matching the marker
 * conventions MapLocationPicker_v2 already established on the app's other maps:
 * navy for a plain point, brand orange for the interactive/aggregate one.
 *
 * A cluster bubble is ALWAYS `CLUSTER_COLOR`. It used to switch to the alarm
 * color when most of its members had alarms, which made the map contradict its
 * own legend swatch — a bubble is an aggregate, not a meter, so it gets one
 * unambiguous color of its own.
 */
const TOP_COLOR = "#14304D"; // primary-80
const ALARM_COLOR = "#DA3B3B"; // error-70
const CLUSTER_COLOR = "#F58220"; // secondary-70
const CLUSTER_LABEL_COLOR = "#0C2239"; // primary-100 — readable on the orange
const MARKER_OUTLINE = "#FFFFFF";

/** Which group of meters the legend is currently isolating. */
type TMapFilter = "all" | "top" | "alarm";

type TProps = {
  points: TMeterMapPoint[];
  /** Height of the map canvas in pixels. */
  height?: number;
  className?: string;
  isLoading?: boolean;
};

/**
 * Map of the meters returned by the overview service.
 *
 * Below `CLUSTER_OFF_ZOOM` nearby meters aggregate into a counted bubble;
 * at or above it clustering is switched off and every meter shows as its own
 * dot. Clicking a cluster zooms into it, clicking a meter opens its popup, and
 * the legend doubles as a filter — clicking a group isolates it.
 *
 * Two implementation notes worth keeping in mind before changing this file:
 *
 * 1. No OpenLayers stylesheet is loaded anywhere in this app (the Neshan SDK
 *    ships a `style.css` nothing imports, and it lacks the `.ol-*` rules
 *    anyway). So OL's `Overlay` has no positioned, full-size, pointer-events
 *    container to sit in and popups land in the wrong place. The popup here is
 *    therefore a plain absolutely-positioned React element, moved from
 *    `getPixelFromCoordinate` on every `postrender` — which also puts it in our
 *    own stacking context, above the legend and zoom controls.
 * 2. All view positioning is synchronous (`updateSize` → `setCenter`/`setZoom`,
 *    or `fit` with no duration). MapLocationPicker_v2 documents why: an animated
 *    view change gets dropped by the OL render loop in production and leaves the
 *    map stuck where it was.
 */
export function MetersMap({
  points,
  height = 520,
  className,
  isLoading,
}: TProps) {
  const { t } = useTranslation();
  const { navigateWithQueryParams } = useURLTools();

  const mapRef = useRef<NeshanMapRef | null>(null);
  const sourceRef = useRef<VectorSource | null>(null);
  const clusterRef = useRef<Cluster | null>(null);
  const popupRef = useRef<HTMLDivElement | null>(null);
  /** Map coordinate the popup is pinned to, read by the postrender handler. */
  const selectedCoordRef = useRef<number[] | null>(null);
  /** Signature of the plotted points — stops the 60s silent poll re-framing the view. */
  const framedSignatureRef = useRef<string>("");

  /**
   * The OL map, published to state in onInit so effects get a clean "map is
   * ready" dependency — the same pattern MapLocationPicker_v2 uses, and what
   * makes the feature sync deterministic regardless of whether the data arrived
   * before or after the map finished initializing.
   */
  const [map, setMap] = useState<Map | null>(null);
  const [selected, setSelected] = useState<TMeterMapPoint | null>(null);
  const [filter, setFilter] = useState<TMapFilter>("all");
  const [zoom, setZoom] = useState<number>(11);

  const validPoints = useMemo(
    () =>
      points.filter(
        (point) =>
          Number.isFinite(point.latitude) &&
          Number.isFinite(point.longitude) &&
          (point.latitude !== 0 || point.longitude !== 0)
      ),
    [points]
  );

  const counts = useMemo(
    () => ({
      top: validPoints.filter((point) => point.isTopConsumer).length,
      alarm: validPoints.filter((point) => point.hasAlarm).length,
    }),
    [validPoints]
  );

  /** Points actually plotted, after the legend filter. */
  const shownPoints = useMemo(() => {
    if (filter === "top") return validPoints.filter((p) => p.isTopConsumer);
    if (filter === "alarm") return validPoints.filter((p) => p.hasAlarm);
    return validPoints;
  }, [validPoints, filter]);

  const closePopup = useCallback(() => {
    selectedCoordRef.current = null;
    setSelected(null);
  }, []);

  /** Cluster bubble / single marker styling. */
  const featureStyle = useCallback((feature: any) => {
    const clustered: any[] = feature.get("features") || [];
    const count = clustered.length;

    if (count > 1) {
      return new Style({
        image: new CircleStyle({
          radius: Math.min(24, 11 + Math.sqrt(count) * 3),
          fill: new Fill({ color: CLUSTER_COLOR }),
          stroke: new Stroke({ color: MARKER_OUTLINE, width: 2 }),
        }),
        text: new Text({
          text: String(count),
          fill: new Fill({ color: CLUSTER_LABEL_COLOR }),
          font: "bold 11px IRANYekanX, Tahoma, sans-serif",
        }),
      });
    }

    const meter: TMeterMapPoint | undefined = clustered[0]?.get("meter");
    return new Style({
      image: new CircleStyle({
        radius: 7,
        fill: new Fill({ color: meter?.hasAlarm ? ALARM_COLOR : TOP_COLOR }),
        stroke: new Stroke({ color: MARKER_OUTLINE, width: 2 }),
      }),
    });
  }, []);

  const onInit = useCallback(
    (mapInstance: Map) => {
      mapInstance.setMapType(NESHAN_MAP_TYPE);

      const source = new VectorSource();
      sourceRef.current = source;

      const cluster = new Cluster({
        source,
        distance: CLUSTER_DISTANCE,
        minDistance: CLUSTER_MIN_DISTANCE,
      });
      clusterRef.current = cluster;

      mapInstance.addLayer(
        new VectorLayer({ source: cluster, style: featureStyle })
      );

      mapInstance.on("click", (event: any) => {
        let hit: any = null;
        mapInstance.forEachFeatureAtPixel(
          event.pixel,
          (feature: any) => {
            if (!hit) hit = feature;
          },
          {}
        );

        if (!hit) {
          closePopup();
          return;
        }

        const clustered: any[] = hit.get("features") || [];

        // A cluster: zoom into what it holds rather than picking one arbitrarily.
        if (clustered.length > 1) {
          closePopup();
          const extent = boundingExtent(
            clustered.map((item) => item.getGeometry().getCoordinates())
          );
          const view = mapInstance.getView();
          mapInstance.updateSize();

          // Animated: a user gesture on an already-visible, already-sized map,
          // which MapLocationPicker_v2 calls out as safe to animate. Only
          // *programmatic* positioning has to stay synchronous.
          const isCoLocated =
            getWidth(extent) < 1e-6 && getHeight(extent) < 1e-6;

          if (isCoLocated) {
            // Nothing to spread out — the members share a coordinate. Go past
            // the clustering threshold so they at least render as real dots.
            view.animate({
              center: getCenter(extent),
              zoom: CLUSTER_OFF_ZOOM,
              duration: 400,
              easing: easeOut,
            });
            return;
          }

          // Fit the members' own extent: a blind `zoom + 2` landed on a view
          // where neighbouring bubbles had split too, so a bubble labelled 3
          // appeared to open onto 6 dots.
          view.fit(extent, {
            padding: [72, 72, 72, 72],
            maxZoom: MAX_ZOOM,
            duration: 400,
            easing: easeOut,
          });
          return;
        }

        const feature = clustered[0] || hit;
        const meter: TMeterMapPoint | undefined = feature.get("meter");
        if (!meter) return;
        selectedCoordRef.current = feature.getGeometry().getCoordinates();
        setSelected(meter);
      });

      mapInstance.on("pointermove", (event: any) => {
        let hovering = false;
        mapInstance.forEachFeatureAtPixel(
          event.pixel,
          () => {
            hovering = true;
          },
          {}
        );
        mapInstance.getViewport().style.cursor = hovering ? "pointer" : "";
      });

      // Guards against the map having been created before the band it sits in
      // settled its height, which otherwise leaves a zero-sized canvas.
      mapInstance.updateSize();

      setMap(mapInstance);
    },
    [closePopup, featureStyle]
  );

  /**
   * Frame whatever is currently plotted (falling back to the full set when a
   * filter hides everything). Shared by the initial data framing and the
   * recenter button — hence the `animate` flag: programmatic framing must stay
   * synchronous, a user gesture on a visible map may animate.
   */
  const frameToPoints = useCallback(
    (options?: { animate?: boolean }) => {
      if (!map) return;
      const target = shownPoints.length > 0 ? shownPoints : validPoints;
      if (target.length === 0) return;

      const coordinates = target.map((point) =>
        fromLonLat([point.longitude, point.latitude])
      );
      const view = map.getView();
      map.updateSize();
      const animate = options?.animate ?? false;

      if (coordinates.length === 1) {
        if (animate) {
          view.animate({
            center: coordinates[0],
            zoom: 15,
            duration: 400,
            easing: easeOut,
          });
        } else {
          view.setCenter(coordinates[0]);
          view.setZoom(15);
        }
        return;
      }

      view.fit(boundingExtent(coordinates), {
        padding: [56, 56, 56, 56],
        maxZoom: 15,
        ...(animate ? { duration: 400, easing: easeOut } : {}),
      });
    },
    [map, shownPoints, validPoints]
  );

  // Keep the plotted features in sync with the data + filter, and frame the
  // full point set once per data change (never on a filter change).
  useEffect(() => {
    const source = sourceRef.current;
    if (!map || !source) return;

    source.clear();
    source.addFeatures(
      shownPoints.map((point) => {
        const feature = new Feature({
          geometry: new Point(fromLonLat([point.longitude, point.latitude])),
        });
        feature.set("meter", point);
        return feature;
      })
    );

    // Drop a popup whose meter is no longer plotted.
    setSelected((current) => {
      if (!current) return current;
      const stillThere = shownPoints.some((point) => point.id === current.id);
      if (!stillThere) selectedCoordRef.current = null;
      return stillThere ? current : null;
    });

    if (validPoints.length === 0) return;

    const signature = validPoints
      .map((point) => point.id)
      .sort()
      .join("|");
    if (signature === framedSignatureRef.current) return;
    framedSignatureRef.current = signature;

    frameToPoints();
  }, [map, shownPoints, validPoints, frameToPoints]);

  // Track zoom, and switch clustering off once the view is tight enough that
  // every meter can have its own dot.
  useEffect(() => {
    if (!map) return;
    const view = map.getView();
    const onResolutionChange = () => {
      const current = view.getZoom() ?? MIN_ZOOM;
      clusterRef.current?.setDistance(
        current >= CLUSTER_OFF_ZOOM ? 0 : CLUSTER_DISTANCE
      );
      const rounded = Math.round(current);
      setZoom((previous) => (previous === rounded ? previous : rounded));
    };
    view.on("change:resolution", onResolutionChange);
    onResolutionChange();
    return () => view.un("change:resolution", onResolutionChange);
  }, [map]);

  // Move the popup with the map. Written straight to the DOM node so panning
  // and zooming never trigger a React render.
  useEffect(() => {
    if (!map) return;
    const reposition = () => {
      const element = popupRef.current;
      if (!element) return;
      const coordinate = selectedCoordRef.current;
      if (!coordinate) {
        element.style.visibility = "hidden";
        return;
      }
      const pixel = map.getPixelFromCoordinate(coordinate);
      const size = map.getSize();
      if (!pixel || !size) {
        element.style.visibility = "hidden";
        return;
      }
      const isOffScreen =
        pixel[0] < 0 || pixel[1] < 0 || pixel[0] > size[0] || pixel[1] > size[1];
      element.style.visibility = isOffScreen ? "hidden" : "visible";
      element.style.transform = `translate(-50%, -100%) translate(${pixel[0]}px, ${
        pixel[1] - 14
      }px)`;
    };

    map.on("postrender", reposition);
    reposition();
    return () => map.un("postrender", reposition);
  }, [map, selected]);

  // The section can change height (siblings loading in, window resize); OL needs
  // to be told, or the canvas keeps its stale size.
  useEffect(() => {
    if (!map) return;
    const onResize = () => map.updateSize();
    window.addEventListener("resize", onResize);
    const raf = requestAnimationFrame(onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(raf);
    };
  }, [map]);

  const changeZoom = (delta: number) => {
    if (!map) return;
    const view = map.getView();
    map.updateSize();
    const next = (view.getZoom() ?? 11) + delta;
    // Animated for the same reason as the cluster zoom above: user gesture on a
    // visible, sized map. `setZoom` here is what made the buttons feel steppy
    // next to the (natively animated) mouse wheel.
    view.animate({
      zoom: Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next)),
      duration: 250,
      easing: easeOut,
    });
  };

  /** Clicking the active group clears the filter; clicking another isolates it. */
  const toggleFilter = (next: Exclude<TMapFilter, "all">) =>
    setFilter((current) => (current === next ? "all" : next));

  /** Always a new tab: the map is a workspace, so drilling into one meter
   *  should never cost the user the view they had set up. */
  const openDetails = (meterId: string) =>
    navigateWithQueryParams(`/dashboard/meters/details/${meterId}`, [], {
      openInNewTab: true,
    });

  const isClustering = zoom < CLUSTER_OFF_ZOOM;

  return (
    <div
      // minHeight as well as height: this sits inside a flex column, where a
      // shrinkable ancestor would otherwise collapse the map to zero and its
      // overflow-hidden would hide that it happened.
      className={clsx("relative w-full shrink-0", className)}
      style={{ height, minHeight: height }}
    >
      <NeshanMap
        className="w-full h-full"
        style={{ direction: "rtl" }}
        ref={mapRef}
        defaultType={NESHAN_MAP_TYPE}
        mapKey={NESHAN_MAP_KEY}
        center={FALLBACK_CENTER}
        zoom={11}
        onInit={onInit}
        poi={false}
        traffic={false}
      />

      {/* Zoom controls — the SDK builds the map with `controls: []`, so these
          replace the OL zoom control it never adds. */}
      <div className="absolute top-3 left-3 z-10 flex flex-col overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg">
        <button
          type="button"
          aria-label={t("zoomIn")}
          title={t("zoomIn")}
          disabled={zoom >= MAX_ZOOM}
          onClick={() => changeZoom(1)}
          className="flex h-8 w-8 items-center justify-center text-primary-90 transition-colors hover:bg-primary-10 disabled:cursor-not-allowed disabled:text-gray-40"
        >
          <Icon icon="MdAdd" className="text-lg" />
        </button>
        <span className="h-px bg-neutral-200" />
        <button
          type="button"
          aria-label={t("zoomOut")}
          title={t("zoomOut")}
          disabled={zoom <= MIN_ZOOM}
          onClick={() => changeZoom(-1)}
          className="flex h-8 w-8 items-center justify-center text-primary-90 transition-colors hover:bg-primary-10 disabled:cursor-not-allowed disabled:text-gray-40"
        >
          <Icon icon="MdRemove" className="text-lg" />
        </button>
        <span className="h-px bg-neutral-200" />
        {/* Way back: after panning off, this re-frames the meters in view. */}
        <button
          type="button"
          aria-label={t("recenterOnMeters")}
          title={t("recenterOnMeters")}
          disabled={validPoints.length === 0}
          onClick={() => frameToPoints({ animate: true })}
          className="flex h-8 w-8 items-center justify-center text-primary-90 transition-colors hover:bg-primary-10 disabled:cursor-not-allowed disabled:text-gray-40"
        >
          <Icon icon="MdCenterFocusStrong" className="text-lg" />
        </button>
      </div>

      {/* Legend — also the filter. */}
      <div className="absolute bottom-3 right-3 z-10 rounded-md border border-neutral-200 bg-white/95 p-2 shadow-lg">
        <div className="flex flex-col gap-0.5">
          {/* A group with nothing in it gets no row — pages that plot only one
              kind of point (e.g. the meters report's top-N map) would otherwise
              show a permanently empty filter. */}
          {(counts.top > 0 || counts.alarm === 0) && (
            <LegendButton
              color={TOP_COLOR}
              label={t("mapLegendTopConsumer")}
              count={counts.top}
              isActive={filter === "top"}
              isDimmed={filter === "alarm"}
              title={t("filterByLegendHint")}
              onClick={() => toggleFilter("top")}
            />
          )}
          {counts.alarm > 0 && (
            <LegendButton
              color={ALARM_COLOR}
              label={t("mapLegendAlarm")}
              count={counts.alarm}
              isActive={filter === "alarm"}
              isDimmed={filter === "top"}
              title={t("filterByLegendHint")}
              onClick={() => toggleFilter("alarm")}
            />
          )}
          <span
            className="flex items-center gap-2 whitespace-nowrap px-1.5 py-1 text-tiny text-gray-90"
            title={t("clusterLegendHint")}
          >
            <span
              className="inline-block h-4 w-4 shrink-0 rounded-full border-2 border-white"
              style={{
                backgroundColor: CLUSTER_COLOR,
                boxShadow: "0 0 0 1px rgba(20,48,77,0.2)",
                opacity: isClustering ? 1 : 0.35,
              }}
            />
            {t("mapLegendCluster")}
            {!isClustering && ` — ${t("clusteringOff")}`}
          </span>

          {filter !== "all" && (
            <button
              type="button"
              onClick={() => setFilter("all")}
              className="mt-1 rounded px-1.5 py-1 text-tiny font-medium text-primary-80 transition-colors hover:bg-primary-10"
            >
              {t("showAllMeters")}
            </button>
          )}
        </div>
      </div>

      {/* Nothing to plot */}
      {!isLoading && shownPoints.length === 0 && (
        <div className="absolute inset-x-0 top-3 z-10 mx-auto w-fit rounded-md border border-neutral-200 bg-white/95 px-3 py-1.5 text-tiny text-gray-90 shadow-lg">
          {t("noMeterLocationsInRange")}
        </div>
      )}

      {/* Popup — positioned by the postrender handler above, and rendered last
          so it stacks over the legend and zoom controls. */}
      {selected && (
        <div
          ref={popupRef}
          className="absolute left-0 top-0 z-20 min-w-[220px] max-w-[280px] rounded-lg border border-neutral-200 bg-white p-3 shadow-xl"
          style={{ visibility: "hidden" }}
        >
          <div className="mb-2 flex items-start justify-between gap-2">
            <span className="text-xs font-bold text-primary-90">
              {selected.identifierId}
            </span>
            <button
              type="button"
              aria-label={t("close")}
              className="shrink-0 text-gray-70 hover:text-gray-130"
              onClick={closePopup}
            >
              <Icon icon="MdClose" className="text-base" />
            </button>
          </div>

          <dl className="flex flex-col gap-1 text-tiny">
            {selected.isTopConsumer && (
              <PopupRow
                label={t("consumptionCubicMeter")}
                value={(selected.consumption ?? 0).toLocaleString("fa-IR")}
              />
            )}
            {selected.hasAlarm && (
              <PopupRow
                label={t("alarmType")}
                value={selected.alarmLabel || "-"}
                tone="alarm"
              />
            )}
          </dl>

          <button
            type="button"
            className="mt-3 w-full rounded-md bg-primary-80 px-2 py-1.5 text-tiny font-medium text-white transition-colors hover:bg-primary-90"
            onClick={() => openDetails(selected.id)}
          >
            {t("viewMeterDetails")}
          </button>

          {/* Pointer down to the anchored dot. */}
          <span className="absolute -bottom-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-b border-e border-neutral-200 bg-white" />
        </div>
      )}
    </div>
  );
}

function LegendButton({
  color,
  label,
  count,
  isActive,
  isDimmed,
  title,
  onClick,
}: {
  color: string;
  label: string;
  count: number;
  isActive: boolean;
  isDimmed: boolean;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={isActive}
      onClick={onClick}
      className={clsx(
        "flex items-center gap-2 whitespace-nowrap rounded px-1.5 py-1 text-tiny transition-colors",
        isActive
          ? "bg-primary-10 font-bold text-primary-90"
          : "text-gray-110 hover:bg-neutral-100",
        isDimmed && "opacity-45"
      )}
    >
      <span
        className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2 border-white"
        style={{
          backgroundColor: color,
          boxShadow: "0 0 0 1px rgba(20,48,77,0.2)",
        }}
      />
      {label}
      <span className="text-gray-70">({count.toLocaleString("fa-IR")})</span>
    </button>
  );
}

function PopupRow({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "alarm";
}) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-2">
      <dt className="shrink-0 text-gray-90">{label}</dt>
      <dd
        className={clsx(
          "text-left font-medium",
          tone === "alarm" ? "text-error-80" : "text-gray-130"
        )}
      >
        {value}
      </dd>
    </div>
  );
}

export default MetersMap;
